import { useEffect, useMemo, useState } from 'react'
import { Workflow } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import ProcessFlowCanvas from '@/frontend/features/Fde/ProcessFlowCanvas'
import { layoutProcessFlow, seedProcessFlow } from '@/frontend/features/Fde/layoutProcessFlow'
import type { FdeCopy, ModelElement, ProcessCanvasNodeDefinition, SelectedProcess } from '@/frontend/features/Fde/types'

interface Props {
  process: SelectedProcess | null
  elements: ModelElement[]
  copy: FdeCopy
}

export default function ProcessDiagramPreview({ process, elements, copy }: Props) {
  const processKey = process ? `${process.path}:${process.repository_revision}` : ''
  const seededNodes = useMemo(
    () => process ? seedProcessFlow(process.definition.nodes) : null,
    [process, processKey],
  )
  const [organized, setOrganized] = useState<{ key: string; nodes: ProcessCanvasNodeDefinition[] } | null>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const nodes = organized?.key === processKey ? organized.nodes : seededNodes

  useEffect(() => {
    let active = true
    setSelectedNodeId(null)
    setSelectedEdgeId(null)
    if (!process) return () => { active = false }

    void layoutProcessFlow(process.definition.nodes, process.definition.edges)
      .then((layout) => { if (active) setOrganized({ key: processKey, nodes: layout }) })
      .catch(() => { /* The deterministic seed remains usable if the layout engine is unavailable. */ })
    return () => { active = false }
  }, [process, processKey])

  const modeLabel = process?.mode_options.find((option) => option.value === process.mode)?.label

  return <section data-testid="process-diagram-preview">
    <div className="mb-2 flex items-center justify-between gap-3">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{copy.workspace.process_diagram_preview}</h3>
      {modeLabel && <Badge variant="outline" className="text-[9px]">{modeLabel}</Badge>}
    </div>
    <div className="relative h-80 overflow-hidden rounded-xl border border-border bg-muted/10">
      {!process || nodes === null ? <div className="flex h-full items-center justify-center text-xs text-muted-foreground">{copy.workspace.process_preview_loading}</div> : nodes.length === 0 ? <div className="flex h-full items-center justify-center p-8 text-center"><div><Workflow className="mx-auto size-6 text-muted-foreground/60" /><p className="mt-2 text-xs font-medium text-foreground">{copy.workspace.process_empty}</p></div></div> : <ProcessFlowCanvas key={`${processKey}:${organized?.key === processKey ? 'organized' : 'seed'}`} nodes={nodes} edges={process.definition.edges} elements={elements} dataCoverage={process.data_coverage} selectedNodeId={selectedNodeId} selectedEdgeId={selectedEdgeId} editable={false} copy={copy} onNodesChange={() => {}} onEdgesChange={() => {}} onSelectNode={setSelectedNodeId} onSelectEdge={setSelectedEdgeId} />}
    </div>
  </section>
}
